import { format } from 'date-fns'
import {
  Search,
  Download,
  Activity,
  CheckCircle,
  XCircle,
  AlertCircle,
  Clock,
  Target,
  TrendingUp,
  Brain,
  Zap
} from 'lucide-react'
import React, { useState, useEffect, useCallback } from 'react'
import { toast } from 'sonner'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { AgentLog } from '@/stores/agentStore'

interface AgentLogsDialogProps {
  isOpen: boolean
  onClose: () => void
  agentId: string
  agentName: string
  projectId: string
}

interface LogFilter {
  action: string
  result: string
  dateRange: string
  search: string
  minScore: number
  maxScore: number
}

const AgentLogsDialog: React.FC<AgentLogsDialogProps> = ({
  isOpen,
  onClose,
  agentId,
  agentName,
  projectId
}) => {
  const [logs, setLogs] = useState<AgentLog[]>([])
  const [filteredLogs, setFilteredLogs] = useState<AgentLog[]>([])
  const [loading, setLoading] = useState(false)
  const [filter, setFilter] = useState<LogFilter>({
    action: 'all',
    result: 'all',
    dateRange: 'all',
    search: '',
    minScore: 0,
    maxScore: 100
  })

  // Performance Analytics
  const [analytics, setAnalytics] = useState({
    averageScore: 0,
    totalActions: 0,
    successRate: 0,
    averageExecutionTime: 0,
    topActions: [] as { action: string; count: number; avgScore: number }[]
  })

  // Fetch logs
  const fetchLogs = useCallback(async () => {
    if (!agentId || !projectId) return
    
    setLoading(true)
    try {
      const response = await fetch(`/api/projects/${projectId}/agents/${agentId}/logs`)
      if (!response.ok) {
        throw new Error(`Failed to fetch logs: ${response.status}`)
      }
      const logsData = await response.json()
      setLogs(logsData)
      calculateAnalytics(logsData)
    } catch (error) {
      console.error('Failed to fetch agent logs:', error)
      toast.error('Failed to load agent logs')
    } finally {
      setLoading(false)
    }
  }, [agentId, projectId])

  // Calculate performance analytics
  const calculateAnalytics = (logsData: AgentLog[]) => {
    if (logsData.length === 0) {
      setAnalytics({
        averageScore: 0,
        totalActions: 0,
        successRate: 0,
        averageExecutionTime: 0,
        topActions: []
      })
      return
    }    const validLogs = logsData.filter(log => log.score !== undefined && log.score !== null)
    const successfulLogs = logsData.filter(log => 
      log.result === 'success' || (log.score && log.score >= 80)
    )
    
    const averageScore = validLogs.length > 0 
      ? validLogs.reduce((sum, log) => sum + (log.score ?? 0), 0) / validLogs.length 
      : 0

    const averageExecutionTime = logsData
      .filter(log => log.executionTime)
      .reduce((sum, log) => sum + (log.executionTime ?? 0), 0) / logsData.length || 0

    // Calculate top actions
    const actionCounts = new Map<string, { count: number; totalScore: number; scores: number[] }>()
      logsData.forEach(log => {
      const existing = actionCounts.get(log.action) ?? { count: 0, totalScore: 0, scores: [] }
      existing.count++
      if (log.score !== undefined && log.score !== null) {
        existing.totalScore += log.score
        existing.scores.push(log.score)
      }
      actionCounts.set(log.action, existing)
    })

    const topActions = Array.from(actionCounts.entries())
      .map(([action, data]) => ({
        action,
        count: data.count,
        avgScore: data.scores.length > 0 ? data.totalScore / data.scores.length : 0
      }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5)

    setAnalytics({
      averageScore: Math.round(averageScore * 10) / 10,
      totalActions: logsData.length,
      successRate: Math.round((successfulLogs.length / logsData.length) * 100),
      averageExecutionTime: Math.round(averageExecutionTime * 100) / 100,
      topActions
    })
  }

  // Filter logs based on current filter settings
  useEffect(() => {
    let filtered = [...logs]

    // Filter by action
    if (filter.action !== 'all') {
      filtered = filtered.filter(log => log.action === filter.action)
    }

    // Filter by result
    if (filter.result !== 'all') {
      filtered = filtered.filter(log => log.result === filter.result)
    }

    // Filter by search term
    if (filter.search) {
      const searchLower = filter.search.toLowerCase()
      filtered = filtered.filter(log => 
        log.action.toLowerCase().includes(searchLower) ||
        log.details?.toLowerCase().includes(searchLower) ||
        log.reasoning?.toLowerCase().includes(searchLower) ||
        log.targetNodeId?.toLowerCase().includes(searchLower)
      )
    }

    // Filter by score range
    filtered = filtered.filter(log => {
      if (log.score === undefined || log.score === null) return true
      return log.score >= filter.minScore && log.score <= filter.maxScore
    })

    // Filter by date range
    if (filter.dateRange !== 'all') {
      const now = new Date()
      const cutoffDate = new Date()
      
      switch (filter.dateRange) {
        case 'today':
          cutoffDate.setHours(0, 0, 0, 0)
          break
        case 'week':
          cutoffDate.setDate(now.getDate() - 7)
          break
        case 'month':
          cutoffDate.setMonth(now.getMonth() - 1)
          break
      }
      
      filtered = filtered.filter(log => new Date(log.createdAt) >= cutoffDate)
    }

    // Sort by most recent first
    filtered.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    
    setFilteredLogs(filtered)
  }, [logs, filter])
  // Load logs when dialog opens
  useEffect(() => {
    if (isOpen) {
      void fetchLogs()
    }
  }, [isOpen, agentId, projectId, fetchLogs])

  // Export logs to JSON
  const handleExportLogs = () => {
    const exportData = {
      agent: {
        id: agentId,
        name: agentName
      },
      exportDate: new Date().toISOString(),
      analytics,
      logs: filteredLogs
    }

    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${agentName.replace(/[^a-z0-9]/gi, '_')}_logs_${format(new Date(), 'yyyy-MM-dd')}.json`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
    
    toast.success('Logs exported successfully')
  }

  // Get unique actions for filter dropdown
  const uniqueActions = Array.from(new Set(logs.map(log => log.action))).sort()

  // Get score color based on value
  const getScoreColor = (score?: number) => {
    if (score === undefined || score === null) return 'bg-gray-500'
    if (score >= 90) return 'bg-green-500'
    if (score >= 80) return 'bg-blue-500'
    if (score >= 70) return 'bg-yellow-500'
    if (score >= 60) return 'bg-orange-500'
    return 'bg-red-500'
  }

  // Get result icon
  const getResultIcon = (result: string, score?: number) => {
    if (result === 'success' || (score && score >= 80)) {
      return <CheckCircle className="w-4 h-4 text-green-500" />
    }
    if (result === 'error' || (score && score < 50)) {
      return <XCircle className="w-4 h-4 text-red-500" />
    }
    return <AlertCircle className="w-4 h-4 text-yellow-500" />
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose} modal>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            <Activity className="w-5 h-5" />
            <span>Agent Logs - {agentName}</span>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Analytics Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card>
              <CardHeader className="pb-2" hasCardAction={false}>
                <CardTitle className="text-sm flex items-center space-x-1">
                  <TrendingUp className="w-4 h-4" />
                  <span>Avg Score</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{analytics.averageScore}</div>
                <div className="text-xs text-gray-500">out of 100</div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2" hasCardAction={false}>
                <CardTitle className="text-sm flex items-center space-x-1">
                  <Target className="w-4 h-4" />
                  <span>Success Rate</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{analytics.successRate}%</div>
                <div className="text-xs text-gray-500">{analytics.totalActions} total</div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2" hasCardAction={false}>
                <CardTitle className="text-sm flex items-center space-x-1">
                  <Clock className="w-4 h-4" />
                  <span>Avg Time</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{analytics.averageExecutionTime}s</div>
                <div className="text-xs text-gray-500">execution</div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2" hasCardAction={false}>
                <CardTitle className="text-sm flex items-center space-x-1">
                  <Zap className="w-4 h-4" />
                  <span>Top Action</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-sm font-bold truncate">
                  {analytics.topActions[0]?.action || 'None'}
                </div>
                <div className="text-xs text-gray-500">
                  {analytics.topActions[0]?.count || 0} times
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Filters */}
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4 p-4 bg-gray-50 rounded-lg">
            <div className="relative">
              <Search className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search logs..."
                value={filter.search}
                onChange={(e) => setFilter(prev => ({ ...prev, search: e.target.value }))}
                className="pl-10"
              />
            </div>

            <Select value={filter.action} onValueChange={(value) => setFilter(prev => ({ ...prev, action: value }))}>
              <SelectTrigger>
                <SelectValue placeholder="Action" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Actions</SelectItem>
                {uniqueActions.map(action => (
                  <SelectItem key={action} value={action}>{action}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={filter.result} onValueChange={(value) => setFilter(prev => ({ ...prev, result: value }))}>
              <SelectTrigger>
                <SelectValue placeholder="Result" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Results</SelectItem>
                <SelectItem value="success">Success</SelectItem>
                <SelectItem value="error">Error</SelectItem>
                <SelectItem value="warning">Warning</SelectItem>
              </SelectContent>
            </Select>

            <Select value={filter.dateRange} onValueChange={(value) => setFilter(prev => ({ ...prev, dateRange: value }))}>
              <SelectTrigger>
                <SelectValue placeholder="Date Range" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Time</SelectItem>
                <SelectItem value="today">Today</SelectItem>
                <SelectItem value="week">Past Week</SelectItem>
                <SelectItem value="month">Past Month</SelectItem>
              </SelectContent>
            </Select>

            <div className="flex space-x-2">
              <Input
                type="number"
                placeholder="Min Score"
                min="0"
                max="100"
                value={filter.minScore}
                onChange={(e) => setFilter(prev => ({ ...prev, minScore: parseInt(e.target.value) || 0 }))}
                className="w-24"
              />
              <Input
                type="number"
                placeholder="Max Score"
                min="0"
                max="100"
                value={filter.maxScore}
                onChange={(e) => setFilter(prev => ({ ...prev, maxScore: parseInt(e.target.value) || 100 }))}
                className="w-24"
              />
            </div>

            <Button onClick={handleExportLogs} variant="outline">
              <Download className="w-4 h-4 mr-1" />
              Export
            </Button>
          </div>

          {/* Logs List */}
          <ScrollArea className="h-[500px]">
            {loading ? (
              <div className="flex items-center justify-center h-32">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
              </div>
            ) : filteredLogs.length === 0 ? (
              <div className="text-center text-gray-500 py-8">
                No logs found matching your filters
              </div>
            ) : (
              <div className="space-y-2">
                {filteredLogs.map((log) => (
                  <Card key={log.id} className="hover:shadow-md transition-shadow">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-2">
                            {getResultIcon(log.result, log.score)}
                            <span className="font-medium">{log.action}</span>
                            {log.triggerEvent && (
                              <Badge variant="outline" className="text-xs">
                                {log.triggerEvent}
                              </Badge>
                            )}
                            {log.targetNodeId && (
                              <Badge variant="secondary" className="text-xs">
                                Node: {log.targetNodeId.substring(0, 8)}...
                              </Badge>
                            )}
                          </div>
                          
                          {log.details && (
                            <p className="text-sm text-gray-600 mb-2">{log.details}</p>
                          )}
                          
                          {log.reasoning && (
                            <div className="bg-blue-50 p-2 rounded text-sm mb-2">
                              <div className="flex items-center space-x-1 mb-1">
                                <Brain className="w-4 h-4 text-blue-500" />
                                <span className="font-medium text-blue-700">Reasoning:</span>
                              </div>
                              <p className="text-blue-600">{log.reasoning}</p>
                            </div>
                          )}
                          
                          {(log.expectedOutcome ?? log.actualOutcome) && (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
                              {log.expectedOutcome && (
                                <div>
                                  <span className="font-medium">Expected:</span>
                                  <p className="text-gray-600">{log.expectedOutcome}</p>
                                </div>
                              )}
                              {log.actualOutcome && (
                                <div>
                                  <span className="font-medium">Actual:</span>
                                  <p className="text-gray-600">{log.actualOutcome}</p>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                        
                        <div className="ml-4 text-right">
                          <div className="flex items-center space-x-2 mb-1">
                            {log.score !== undefined && log.score !== null && (
                              <div className={`px-2 py-1 rounded text-white text-xs font-bold ${getScoreColor(log.score)}`}>
                                {log.score}
                              </div>
                            )}
                            {log.executionTime && (
                              <Badge variant="outline" className="text-xs">
                                {log.executionTime}s
                              </Badge>
                            )}
                          </div>
                          <div className="text-xs text-gray-500">
                            {format(new Date(log.createdAt), 'MMM dd, HH:mm:ss')}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </ScrollArea>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default AgentLogsDialog
